/**
 *  @class
 *  @function ProductTabs
 *
 *  One widget, two presentations. At 768px and up it behaves as a tab set:
 *  exactly one panel open, arrow keys move between tabs, ARIA tab roles apply.
 *  Below 768px it behaves as an accordion: panels toggle independently and the
 *  headers are plain disclosure buttons. The DOM is identical in both modes —
 *  only roles, state attributes and CSS change.
 */
if (!customElements.get('product-tabs')) {
	class ProductTabs extends HTMLElement {
		constructor() {
			super();
			this.mediaQuery = window.matchMedia('(min-width: 768px)');
			this.onBreakpointChange = this.onBreakpointChange.bind(this);
		}

		connectedCallback() {
			this.list = this.querySelector('.product-tabs__list');
			this.headers = Array.from(this.querySelectorAll('.product-tabs__header'));
			this.panels = Array.from(this.querySelectorAll('.product-tabs__panel'));

			if (!this.list || !this.headers.length || this.headers.length !== this.panels.length) return;

			this.headers.forEach((header, index) => {
				header.addEventListener('click', () => this.onHeaderClick(index));
				header.addEventListener('keydown', (event) => this.onKeydown(event, index));
			});

			this.mediaQuery.addEventListener('change', this.onBreakpointChange);
			this.applyMode();

			if (window.Shopify && Shopify.designMode) {
				this.addEventListener('shopify:block:select', (event) => {
					const index = this.headers.indexOf(event.target);
					if (index !== -1) this.open(index);
				});
			}
		}

		disconnectedCallback() {
			this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
		}

		get isTabsMode() {
			return this.mediaQuery.matches;
		}

		get openIndex() {
			const index = this.panels.findIndex((panel) => !panel.hidden);
			return index === -1 ? 0 : index;
		}

		onBreakpointChange() {
			this.applyMode();
		}

		applyMode() {
			const tabsMode = this.isTabsMode;
			const openIndex = this.openIndex;

			if (tabsMode) {
				this.list.setAttribute('role', 'tablist');
			} else {
				this.list.removeAttribute('role');
			}

			this.headers.forEach((header, index) => {
				const isOpen = index === openIndex;

				if (tabsMode) {
					header.setAttribute('role', 'tab');
					header.setAttribute('aria-selected', isOpen ? 'true' : 'false');
					header.setAttribute('tabindex', isOpen ? '0' : '-1');
					header.removeAttribute('aria-expanded');
					this.panels[index].setAttribute('role', 'tabpanel');
					this.setPanelState(index, isOpen);
				} else {
					header.removeAttribute('role');
					header.removeAttribute('aria-selected');
					header.removeAttribute('tabindex');
					header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
					this.panels[index].setAttribute('role', 'region');
					this.setPanelState(index, isOpen);
				}
			});
		}

		onHeaderClick(index) {
			if (this.isTabsMode) {
				this.open(index);
			} else {
				this.toggle(index);
			}
		}

		open(index) {
			this.headers.forEach((header, i) => {
				const isOpen = i === index;

				if (this.isTabsMode) {
					header.setAttribute('aria-selected', isOpen ? 'true' : 'false');
					header.setAttribute('tabindex', isOpen ? '0' : '-1');
					this.setPanelState(i, isOpen);
				} else if (isOpen) {
					header.setAttribute('aria-expanded', 'true');
					this.setPanelState(i, true);
				}
			});
		}

		toggle(index) {
			const isOpen = this.panels[index].hidden;
			this.headers[index].setAttribute('aria-expanded', isOpen ? 'true' : 'false');
			this.setPanelState(index, isOpen);
		}

		setPanelState(index, isOpen) {
			this.panels[index].hidden = !isOpen;
			this.panels[index].classList.toggle('active', isOpen);
			this.headers[index].classList.toggle('active', isOpen);
		}

		onKeydown(event, index) {
			if (!this.isTabsMode) return;

			const lastIndex = this.headers.length - 1;
			let target = null;

			switch (event.key) {
				case 'ArrowRight':
					target = index === lastIndex ? 0 : index + 1;
					break;
				case 'ArrowLeft':
					target = index === 0 ? lastIndex : index - 1;
					break;
				case 'Home':
					target = 0;
					break;
				case 'End':
					target = lastIndex;
					break;
				default:
					return;
			}

			event.preventDefault();
			this.open(target);
			this.headers[target].focus();
		}
	}
	customElements.define('product-tabs', ProductTabs);
}
